// Cookie notice. The platform sets only essential cookies today, so both choices behave the same;
// the stored choice exists so any future optional cookie can check hasOptionalConsent() first.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';

const KEY = 'cookie_consent';
const OPEN_EVENT = 'cookie-consent:open';
export type CookieChoice = 'all' | 'essential';

export function readCookieChoice(): CookieChoice | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { choice?: string };
    return parsed.choice === 'all' || parsed.choice === 'essential' ? parsed.choice : null;
  } catch {
    return null;
  }
}

/** True only after the visitor chose "Accept all". Gate any analytics or other optional cookie on this. */
export function hasOptionalConsent(): boolean {
  return readCookieChoice() === 'all';
}

export function openCookieSettings() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export function CookieConsent() {
  const [open, setOpen] = useState(() => readCookieChoice() === null);

  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, show);
    return () => window.removeEventListener(OPEN_EVENT, show);
  }, []);

  const choose = (choice: CookieChoice) => {
    try { localStorage.setItem(KEY, JSON.stringify({ choice, at: new Date().toISOString() })); } catch { /* storage blocked: ask again next visit */ }
    setOpen(false);
  };

  if (!open) return null;
  return (
    <div className="cookie-banner" role="dialog" aria-live="polite" aria-label="Cookie notice">
      <Cookie size={20} aria-hidden className="cookie-icon" />
      <p>
        We use essential cookies to keep you signed in and to protect your account. We do not use advertising or tracking cookies.
        {' '}<Link to="/legal/cookies">Cookie Policy</Link> · <Link to="/legal/privacy">Privacy Policy</Link>
      </p>
      <div className="cookie-actions">
        <button type="button" className="button secondary sm" onClick={() => choose('essential')}>Essential only</button>
        <button type="button" className="button primary sm" onClick={() => choose('all')}>Accept all</button>
      </div>
    </div>
  );
}
